import { Injectable } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Subject, fromEvent } from "rxjs";
import { filter, takeUntil, debounceTime } from "rxjs/operators";
import { HttpClientWrapper } from "./http-client.wrapper";
import { environment } from "../../environments/environment";
import { TranslateService } from "@ngx-translate/core";
import {
  WebTrackingSession,
  WebTrackingDevice,
  WebTrackingData
} from "psa-tnt-models";

@Injectable({
  providedIn: "root"
})
export class WebTrackingService {
  private deviceId: string | null = null;
  private device: WebTrackingDevice | null = null;
  private destroy$ = new Subject<void>();
  private lastVisibilityChange: number = Date.now();
  private isTrackingOn: boolean = true;
  // Cache for browser and OS info (rarely changes)
  private cachedBrowserInfo: string | null = null;
  private cachedOSInfo: string | null = null;

  public user: string | null = null;
  public right: string | null = null;

  private readonly ENDPOINT = environment.apiBaseUrl + "web-tracking";
  private readonly RESIZE_DEBOUNCE_TIME = 600; // 600ms if multiple resize

  constructor(
    private router: Router,
    private httpClientWrapper: HttpClientWrapper<WebTrackingData>,
    private translate: TranslateService
  ) {
    // Track route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.trackAction(`navigation-${event.urlAfterRedirects}`);
      });

    // Track language changes
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.handleLanguageChange(event.lang);
      });

    // Track clicks on elements with tracking attributes
    fromEvent(document, "click")
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        this.handleDocumentClick(event);
      });

    // Track screen resize for device changes
    fromEvent(window, "resize")
      .pipe(debounceTime(this.RESIZE_DEBOUNCE_TIME), takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkDeviceChange();
      });

    // Track page visibility changes
    fromEvent(document, "visibilitychange")
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleVisibilityChange();
      });
  }

  /**
   * Initialize web tracking with user information
   */
  public initializeTracking(user: string, right: string): void {
    this.isTrackingOn = right !== "IBMAdmin";

    if (!this.isTrackingOn) {
      return;
    }

    // Avoid re-initializing with the same user and right
    if (this.isInitialized() && this.user === user && this.right === right) {
      return;
    }

    this.user = user;
    this.right = right;

    try {
      this.initializeDevice();
    } catch (error) {
      console.error("Failed to initialize web tracking:", error);
    }
  }

  /**
   * Stop tracking
   */
  public stopTracking(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Manually track a click event with duration calculation
   */
  public trackClick(elementId: string): void {
    this.trackAction(elementId);
  }

  /**
   * Check if web tracking is properly initialized and ready
   */
  public isInitialized(): boolean {
    return this.deviceId !== null && this.device !== null && this.user !== null;
  }

  /**
   * Attempt to automatically initialize tracking from localStorage
   */
  private tryAutoInitialize(): void {
    try {
      const username = window.localStorage.getItem("username");
      const profilId = window.localStorage.getItem("profilId");

      if (username && profilId) {
        const encryptedUserId = hash(username);
        this.initializeTracking(encryptedUserId, profilId);
      }
    } catch (error) {
      console.error("Auto-initialization failed:", error);
    }
  }

  private calculateVisibilityDuration(): number {
    const now = Date.now();
    const duration = Math.round((now - this.lastVisibilityChange) / 1000);
    this.lastVisibilityChange = now;
    return duration;
  }

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange(): void {
    const duration = this.calculateVisibilityDuration();
    if (document.hidden) {
      // L'utilisateur quitte la page (onglet masqué)
      this.trackAction("page-hidden", duration);
    } else {
      // L'utilisateur revient sur la page (onglet visible)
      this.trackAction("page-visible", duration);
    }
  }

  /**
   * Track any action and send data immediately
   */
  private trackAction(
    action: string,
    duration?: number,
    device?: WebTrackingDevice
  ): void {
    if (!this.isTrackingOn) return;

    if (!this.isInitialized()) {
      this.tryAutoInitialize();
      if (!this.isInitialized()) {
        return;
      }
    }

    const session: WebTrackingSession = {
      act: action,
      device: device?.id,
      sec: duration
    };

    const trackingData: WebTrackingData = {
      userId: this.user!,
      session: session
    };

    if (device) {
      trackingData.device = device;
    }

    this.httpClientWrapper.post(this.ENDPOINT, trackingData).subscribe({
      next: () => {
        // Success - no logging needed
      },
      error: (error) => {
        console.error("Failed to send tracking data:", error);
      }
    });
  }

  /**
   * Initialize device information
   */
  private initializeDevice(): void {
    this.deviceId = this.getDeviceInfo();
    this.device = {
      id: this.deviceId,
      browser: this.getBrowserInfo(),
      os: this.getOSInfo(),
      screen: this.getScreenInfo(),
      lang: this.translate.currentLang || this.translate.getDefaultLang()
    };
  }

  /**
   * Handle document click events - Simple tracking by data-tracking-id
   */
  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Search for data-tracking-id on clicked element and its parents
    let currentElement: HTMLElement | null = target;
    let trackingId: string | null = null;
    let depth = 0;
    const MAX_DEPTH = 8;

    // Walk up the DOM tree to find an element with data-tracking-id
    while (
      currentElement &&
      currentElement !== document.body &&
      depth < MAX_DEPTH
    ) {
      trackingId = currentElement.getAttribute("data-tracking-id");
      if (trackingId?.trim()) {
        this.trackClick(trackingId.trim());
        return;
      }
      currentElement = currentElement.parentElement;
      depth++;
    }

    // For Angular Material tabs, we let Angular event handler do the tracking
    if (this.isAngularMaterialTab(target)) return;
  }

  /**
   * Simple detection of Angular Material tab elements
   */
  private isAngularMaterialTab(element: HTMLElement): boolean {
    const classList = element.className;

    if (typeof classList !== "string") return false;
    return (
      classList.includes("mat-tab-label") ||
      classList.includes("mat-ripple") ||
      classList.includes("mat-focus-indicator")
    );
  }

  private handleLanguageChange(lang: string): void {
    if (!this.device) return;
    if (this.device.lang === lang) return; // Avoid duplicate tracking
    this.device.lang = lang;
    this.trackAction(`language-change-${lang}`, undefined, this.device);
  }

  private checkDeviceChange(): void {
    if (!this.device) return;
    const newDeviceId = this.getDeviceInfo();
    const newScreenInfo = this.getScreenInfo();

    if (this.deviceId !== newDeviceId || this.device.screen !== newScreenInfo) {
      this.deviceId = newDeviceId;
      this.device.id = newDeviceId;
      this.device.screen = newScreenInfo;
      this.trackAction("device-change", undefined, this.device);
    }
  }

  /**
   * Get screen information including zoom level
   */
  private getScreenInfo(): string {
    const zoom = Math.round(window.devicePixelRatio * 100) / 100;
    return `${screen.width}x${screen.height} ${zoom}x`;
  }

  /**
   * Calculate device ID based on current browser, OS and screen info
   */
  private getDeviceInfo(): string {
    const browser = this.getBrowserInfo();
    const os = this.getOSInfo();
    const screen_info = this.getScreenInfo();
    return hash(`${browser}-${os}-${screen_info}`);
  }

  private getBrowserInfo(): string {
    if (this.cachedBrowserInfo) {
      return this.cachedBrowserInfo;
    }

    const ua = navigator.userAgent;
    let browserName = "Unknown";
    let browserVersion = "Unknown";

    // Chrome
    if (ua.includes("Chrome")) {
      browserName = "Chrome";
      const match = ua.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }
    // Firefox
    else if (ua.includes("Firefox")) {
      browserName = "Firefox";
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }
    // Safari
    else if (ua.includes("Safari") && !ua.includes("Chrome")) {
      browserName = "Safari";
      const match = ua.match(/Version\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }
    // Edge
    else if (ua.includes("Edge")) {
      browserName = "Edge";
      const match = ua.match(/Edge\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }

    this.cachedBrowserInfo = `${browserName} ${browserVersion}`;
    return this.cachedBrowserInfo;
  }

  private getOSInfo(): string {
    if (this.cachedOSInfo) {
      return this.cachedOSInfo;
    }

    const ua = navigator.userAgent;
    let osName = "Unknown";
    if (ua.includes("Windows NT 10.0")) osName = "Windows 10";
    else if (ua.includes("Windows NT 6.3")) osName = "Windows 8.1";
    else if (ua.includes("Windows NT 6.2")) osName = "Windows 8";
    else if (ua.includes("Windows NT 6.1")) osName = "Windows 7";
    else if (ua.includes("Windows NT")) osName = "Windows";
    else if (ua.includes("Mac OS X")) {
      const match = ua.match(/Mac OS X (\d+[._]\d+)/);
      osName = match ? `macOS ${match[1].replace("_", ".")}` : "macOS";
    } else if (ua.includes("Linux")) osName = "Linux";
    else if (ua.includes("Android")) osName = "Android";
    else if (ua.includes("iOS")) osName = "iOS";

    this.cachedOSInfo = osName;
    return this.cachedOSInfo;
  }
}

/**
 * Create a simple hash with a salt
 */
export function hash(input: string): string {
  const combined = input + "tnt_salt_2024";
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; //? Convert to 32bit integer
  }
  //? Convert to positive hex string and ensure it's 8 characters
  return Math.abs(hash).toString(16).padStart(8, "0").substring(0, 8);
}
